import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const VisualMessageContext = createContext();

export const useVisualMessage = () => {
  const context = useContext(VisualMessageContext);
  if (!context) {
    throw new Error('useVisualMessage must be used within a VisualMessageProvider');
  }
  return context;
};

export const VisualMessageProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);

  const showMessage = useCallback((text, type = 'success', duration = 4000) => {
    const id = Date.now() + Math.random();
    const message = { id, text, type, duration };
    
    setMessages(prev => [...prev, message]);
    
    // Auto remove after duration
    setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== id));
    }, duration);
    
    return id;
  }, []);

  const removeMessage = useCallback((id) => {
    setMessages(prev => prev.filter(m => m.id !== id));
  }, []);

  const showSuccess = useCallback((text, duration) => {
    return showMessage(text, 'success', duration);
  }, [showMessage]);

  const showError = useCallback((text, duration) => {
    return showMessage(text, 'error', duration);
  }, [showMessage]);

  const showInfo = useCallback((text, duration) => {
    return showMessage(text, 'info', duration);
  }, [showMessage]);

  const showWarning = useCallback((text, duration) => {
    return showMessage(text, 'warning', duration);
  }, [showMessage]);

  const value = useMemo(() => ({
    messages,
    showMessage,
    removeMessage,
    showSuccess,
    showError,
    showInfo,
    showWarning,
  }), [messages, showMessage, removeMessage, showSuccess, showError, showInfo, showWarning]);

  return (
    <VisualMessageContext.Provider value={value}>
      {children}
    </VisualMessageContext.Provider>
  );
};